import { useState } from "react";
import styled from "styled-components";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { Headline, Subheader } from "../../../../Components/Text";
import { ReactComponent as Trophy } from "../../../../Assets/Nfluence/trophy-orange.svg";
import { useUser } from "../../../../Hooks/useUser";
import Countdown from "react-countdown";
import { useHistory } from "react-router-dom";
import { useGateValue } from "@statsig/react-bindings";

import { ILeaderboardDetails } from "../../@types";
import { CampaignFootnote } from "../../../../Components/CampaignFootnote";
import { JoinCampaignButton } from "../JoinCampaignButton";
import { Divider } from "../../../../Components/Divider";
import theme from "../../../../../theme/themeThree";
import { randomAvatar } from "../../../../Utilities/Images";
import { analytics } from "MVVM/Analytics";
import { ClipComments } from "MVVM/Components/Comments/Clip";
import { Tabs } from "MVVM/Components/Tabs";
import { CountDownRenderer } from "../../Views/Play/countdownTimer";

const Leaderboard = ({
  leaderboardDetails,
  showButton,
  showCampaignImage,
  size,
}: {
  leaderboardDetails: ILeaderboardDetails;
  showButton?: boolean;
  showCampaignImage?: boolean;
  size?: "small" | "large";
}) => {
  const { allstarUser } = useUser();
  const usernameTruncateWidth = size === "small" ? "60px" : "250px";
  const [commentCount, setCommentCount] = useState<number>(0);
  const users = leaderboardDetails.topTenContestants;
  const history = useHistory();
  const allowComments = useGateValue("comments_on_competitions");
  const userInLeaderboard = leaderboardDetails.viewerContestant.rank <= 10;
  const tabs = {
    comments: {
      component: (
        <ColumnContainer>
          {leaderboardDetails.campaign.promotionalCampaignClip._id && (
            <ClipComments
              clipId={leaderboardDetails.campaign.promotionalCampaignClip._id}
              setCommentCount={setCommentCount}
            />
          )}
        </ColumnContainer>
      ),
      name: "comments",
      title: `Comments ${
        Number.isInteger(commentCount) ? `(${commentCount})` : ""
      }`,
      type: "Comments",
    },
    leaderboard: {
      component: (
        <Grid mt={3}>
          {showCampaignImage && (
            <Flex
              flexDirection={"column"}
              mb={5}
              style={{ alignItems: "center" }}
            >
              <Image
                width="100%"
                flexShrink={0}
                src={leaderboardDetails.campaign.images.campaign}
                style={{ marginRight: "auto" }}
              />
              <Countdown
                date={new Date(leaderboardDetails.campaign.endsAt)}
                renderer={(props) => (
                  <CountDownRenderer
                    days={props.days}
                    hours={props.hours}
                    minutes={props.minutes}
                    seconds={props.seconds}
                    color={leaderboardDetails.campaign.color || ""}
                    variant="text.labelSmall"
                    showEndsIn={true}
                    showIcon={false}
                  />
                )}
              />
            </Flex>
          )}

          <ListItemHeader>
            <StyledRankHeading color={leaderboardDetails.campaign.color}>
              RANK
            </StyledRankHeading>
            <Flex alignItems={"center"}>
              <StyledUserHeading color={leaderboardDetails.campaign.color}>
                PLAYER
              </StyledUserHeading>
            </Flex>
            <Flex alignItems={"center"} justifyContent={"end"}>
              <StyledScoreHeading color={leaderboardDetails.campaign.color}>
                SCORE
              </StyledScoreHeading>
            </Flex>
          </ListItemHeader>
          {users &&
            users.map((user, index) => {
              if (!user.user) return <></>;
              return (
                <ListItem
                  key={index}
                  color={leaderboardDetails.campaign.color}
                  isViewer={
                    leaderboardDetails.viewerContestant?.user?._id ===
                    user.user._id
                  }
                >
                  <StyledRank>
                    <StyledRankNumber>{index + 1}</StyledRankNumber>
                  </StyledRank>
                  <ProfileFlex
                    marginLeft={["0%", "5%"]}
                    alignItems={"center"}
                    onClick={() => {
                      analytics.track("Leaderboard Profile Clickthrough", {
                        profile: user.user._id,
                        postion: index + 1,
                      });
                      history.push(`/profile?user=${user.user._id}`);
                    }}
                  >
                    <AvatarWithFallback src={`${user.user.avatar}`} />
                    <Flex flexDirection="column" style={{ gap: 4 }}>
                      <StyledHeadline
                        usernameTruncateWidth={usernameTruncateWidth}
                        className="leaderboard-username"
                        color="ice"
                      >
                        {user.user.username}
                      </StyledHeadline>
                      <Text
                        variant="text.labelXSmall"
                        color={leaderboardDetails.campaign.color}
                        style={{
                          marginLeft: 10,
                          fontStyle: "italic",
                          opacity: 0.7,
                        }}
                      >
                        {user.clipsCreated}
                      </Text>
                    </Flex>
                  </ProfileFlex>
                  <Flex alignItems={"center"} justifyContent={"end"}>
                    <Flex
                      style={{
                        width: "100%",
                        justifyContent: "end",
                        height: "25px",
                      }}
                    >
                      {user?.verified &&
                        size !== "small" &&
                        leaderboardDetails?.campaign?.images?.verifiedBadge && (
                          <StyledSvg
                            src={
                              leaderboardDetails?.campaign.images.verifiedBadge
                            }
                          />
                        )}
                      <MaxWidthHeadline
                        textAlign="end"
                        pr={size === "small" ? 0 : 3}
                        campaignColor={leaderboardDetails.campaign.color}
                      >
                        {user.points}
                      </MaxWidthHeadline>
                    </Flex>
                  </Flex>
                </ListItem>
              );
            })}
          {leaderboardDetails.viewerContestant && !userInLeaderboard ? (
            <ListItem isViewer={true} color={leaderboardDetails.campaign.color}>
              <StyledRank>
                <StyledRankNumber>
                  {leaderboardDetails.viewerContestant.rank}
                </StyledRankNumber>
              </StyledRank>
              <ProfileFlex
                marginLeft={["0%", "5%"]}
                alignItems={"center"}
                onClick={() => {
                  analytics.track("Leaderboard Profile Clickthrough", {
                    profile: leaderboardDetails.viewerContestant.user._id,
                    postion: leaderboardDetails.viewerContestant.rank,
                  });
                  history.push(
                    `/profile?user=${leaderboardDetails.viewerContestant.user._id}`,
                  );
                }}
              >
                <AvatarWithFallback src={`${allstarUser?.user?.avatar}`} />

                <Flex flexDirection="column" style={{ gap: 4 }}>
                  <StyledHeadline
                    usernameTruncateWidth={usernameTruncateWidth}
                    className="leaderboard-username"
                    color="ice"
                  >
                    {leaderboardDetails.viewerContestant.user.username}
                  </StyledHeadline>
                  <Text
                    variant="text.labelXSmall"
                    color={leaderboardDetails.campaign.color}
                    style={{
                      marginLeft: 10,
                      fontStyle: "italic",
                      opacity: 0.7,
                    }}
                  >
                    {leaderboardDetails.viewerContestant.clipsCreated}
                  </Text>
                </Flex>
              </ProfileFlex>
              <Flex alignItems={"center"} justifyContent={"end"}>
                <Flex
                  style={{
                    width: "100%",
                    justifyContent: "end",
                    height: "25px",
                  }}
                >
                  {leaderboardDetails.viewerContestant.verified &&
                    leaderboardDetails?.campaign?.images?.verifiedBadge && (
                      <StyledSvg
                        src={leaderboardDetails?.campaign.images.verifiedBadge}
                      />
                    )}
                  <MaxWidthHeadline
                    textAlign="end"
                    pr={size === "small" ? 0 : 3}
                  >
                    {leaderboardDetails.viewerContestant.points}
                  </MaxWidthHeadline>
                </Flex>
              </Flex>
            </ListItem>
          ) : (
            <></>
          )}
          {showButton && (
            <>
              <Divider width="100%" my={1} color={theme.colors.chalkAlpha40} />
              <Flex
                width="100%"
                justifyContent={"center"}
                alignItems={"center"}
                style={{ marginTop: 5 }}
              >
                <JoinCampaignButton
                  slug={leaderboardDetails?.campaign?.slug || ""}
                  color={leaderboardDetails?.campaign?.color || ""}
                  secondaryColor={
                    leaderboardDetails?.campaign?.secondaryColor || ""
                  }
                  width="70%"
                />
              </Flex>
            </>
          )}
        </Grid>
      ),
      name: "leaderboard",
      title: "Leaderboard",
      type: "Leaderboard",
    },
  };

  return (
    <>
      <MaxWidthWrapper>
        <Content>
          <TrophyContainer>
            <TrophyIcon color={leaderboardDetails?.campaign?.color} />
          </TrophyContainer>
          <Tabs
            tabs={
              allowComments
                ? [tabs.leaderboard, tabs.comments]
                : [tabs.leaderboard]
            }
            color={leaderboardDetails?.campaign?.color}
          />
        </Content>
        <CampaignFootnote
          verifiedBadge={leaderboardDetails?.campaign?.images.verifiedBadge}
          partnerName={leaderboardDetails?.campaign?.partnerName}
        />
      </MaxWidthWrapper>
    </>
  );
};

const AvatarWithFallback = ({ src }: { src: string }) => {
  const [missingAvatarChecked, setMissingThumbnailChecked] = useState(false);

  return (
    <Avatar
      src={src}
      onError={(e) => {
        if (missingAvatarChecked) return true;
        e.currentTarget.src = randomAvatar();
        setMissingThumbnailChecked(true);
      }}
    />
  );
};

const ColumnContainer = styled(Box)`
  height: 100%;
  max-height: 750px;
  overflow-y: scroll;
  scrollbar-width: none;
  width: 100%;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const MaxWidthWrapper = styled(Box)`
  max-width: 600px;

  @media (max-width: 1400px) {
    width: 100%;
    max-width: 100%;
  }
`;

const StyledSvg = styled(Image)`
  flex-shrink: 0;
`;

const ProfileFlex = styled(Flex)`
  &:hover {
    cursor: pointer;
  }
  &:hover .leaderboard-username {
    cursor: pointer;
    color: ${(props) => props.theme.colors.envy};
    transition: 0.2s;
  }
  max-width: 200px;
`;

const TrophyIcon = styled(Trophy)`
  width: 45px;
  height: 45px;
`;

const TrophyContainer = styled(Flex)`
  width: 80px;
  height: 80px;
  background: #162129;
  position: absolute;
  top: -45px;
  left: 36px;
  box-shadow: 0 10px 14px 0 ${(props) => props.theme.colors.midnightAlpha60};
  border-radius: 10px;
  align-items: center;
  justify-content: center;
`;

const Content = styled(Flex)`
  background: ${(props) => props.theme.colors.midnightAlpha40};
  border-radius: 20px;
  box-shadow: 0 10px 14px 0 ${(props) => props.theme.colors.midnightAlpha60};
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  margin-right: 10px;
  margin-top: 55px;
  padding: 60px 20px 20px;
  position: relative;
  width: 100%;
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 50px 15px 15px;
  }
  @media (max-width: 1100px) {
    max-width: 100%;
    justify-content: center;
  }
`;

const Grid = styled(Flex)`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  position: relative;
  width: 100%;
`;

const ListItem = styled(Flex)<{
  isViewer?: boolean;
  color?: string;
}>`
  margin: 0;
  padding: 0;
  list-style-type: none;
  display: grid;
  align-items: center;
  grid-template-columns: 40px 3fr 1fr;
  grid-column-gap: 10px;
  color: ${({ theme }) => theme.colors.ice};
  ${({ isViewer, theme, color }) =>
    isViewer
      ? `
        padding-top: 10px;
        padding-bottom: 10px;
        border-radius: 8px;
        border: 1px solid ${color ? color : theme.colors.envyAlpha30};
      `
      : ""};

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    font-size: ${({ theme }) => theme.text.subheader.fontSize};
  }
`;

const ListItemHeader = styled(Flex)`
  margin-bottom: 10px;
  padding-bottom: 10px;
  list-style-type: none;
  display: grid;
  align-items: flex-start;
  grid-template-columns: 40px 3fr 1fr;
  grid-column-gap: 10px;
  border-bottom: 1px solid ${(props) => props.theme.colors.chalkAlpha40};
`;

const Avatar = styled(Image)`
  height: 42px;
  width: 42px;
  border-radius: 999px;
  border: 2px solid ${(props) => props.theme.colors.navy};
  flex-shrink: 0;
  &:first {
    height: 100px;
    width: 100px;
  }
`;

const StyledHeadline = styled(Headline)`
  margin-left: 10px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  text-align: start;
  max-width: 160px;
`;

const MaxWidthHeadline = styled(Headline)`
  width: 100px;
  margin-right: 4px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: end;
  width: 80px;
  flex-shrink: 0;
  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    width: 42px;
  }
`;

const StyledRank = styled(Flex)`
  margin-left: 10px;
  margin-right: 10px;
  background: ${(props) => props.theme.colors.envy};
  border-radius: 10px;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  ${(props: any) => {
    if (props.rankOne) {
      return `
        background: ${props.theme.colors.error};
        `;
    }
    if (props.rankTwo) {
      return `
        background: ${props.theme.colors.envy};
        `;
    }
    if (props.rankThree) {
      return `
        background: ${props.theme.colors.turbo};
        `;
    } else {
      return ` 
        background: transparent;
        `;
    }
  }}
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    margin-left: 0px;
    margin-right: 0px;
  }
`;

const StyledRankNumber = styled(Headline)`
  color: ${(props) => props.theme.colors.ice};
  font-weight: 600;
  margin-right: 5px;
`;

const StyledRankHeading = styled(Subheader)<{ color?: string }>`
  color: ${(props) => (props.color ? props.color : props.theme.colors.envy)};
  font-weight: 600;
  margin-left: 5px;
`;

const StyledUserHeading = styled(Subheader)<{ color?: string }>`
  color: ${(props) => (props.color ? props.color : props.theme.colors.envy)};
  margin-left: 15px;
  font-weight: 600;
`;

const StyledScoreHeading = styled(Subheader)<{ color?: string }>`
  color: ${(props) => (props.color ? props.color : props.theme.colors.envy)};
  margin-left: 15px;
  margin-right: 5px;
  font-weight: 600;
  align-items: flex-end;
`;

export default Leaderboard;
